import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useFirebase } from '../../../context/FirebaseContext';
import {
  getCallHistory,
  getCallHistoryEntry,
  getCallHistoryDailyStats,
  getCallHistoryHourlyStats,
  getHistoryPipelines,
  getCallerTypeDisplayInfo,
  getValidationStatusDisplayInfo,
} from '../../../api/conceptgate';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
  Brush,
} from 'recharts';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import ComputerIcon from '@mui/icons-material/Computer';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import WebhookIcon from '@mui/icons-material/Webhook';
import RestoreIcon from '@mui/icons-material/Restore';
import TablePagination from '@mui/material/TablePagination';
import ValidationCallHistoryModal from './ValidationCallHistoryModal';
import './ValidationHistoryView.css';

const CallerTypeIcon = ({ callerType, size = 'small' }) => {
  const props = { fontSize: size };
  switch (callerType) {
    case 'UI':
      return <ComputerIcon {...props} />;
    case 'MCP':
      return <SmartToyIcon {...props} />;
    case 'WEBHOOK':
      return <WebhookIcon {...props} />;
    default:
      return null;
  }
};

const ValidationHistoryView = ({ onBack, onApplyTrade, isDarkMode }) => {
  const { auth } = useFirebase();
  const [history, setHistory] = useState([]);
  const [dailyStats, setDailyStats] = useState([]);
  const [hourlyStats, setHourlyStats] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [days, setDays] = useState(0); // 0 = Today (hourly), 7/14/30 = days
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [brushRange, setBrushRange] = useState({ startIndex: 0, endIndex: null });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Determine which stats API to call based on filter
      const isToday = days === 0;

      // Load all data in parallel
      const [historyResult, statsResult, pipelinesResult] = await Promise.all([
        new Promise((resolve) => {
          getCallHistory(auth, (result) => {
            if (result.error) resolve({ error: result.error });
            else resolve(result || []);
          }, { limit: 100, callerType: null });
        }),
        new Promise((resolve) => {
          if (isToday) {
            getCallHistoryHourlyStats(auth, (result) => {
              if (result.error) resolve({ error: result.error, isHourly: true });
              else resolve({ data: result || [], isHourly: true });
            });
          } else {
            getCallHistoryDailyStats(auth, (result) => {
              if (result.error) resolve({ error: result.error, isHourly: false });
              else resolve({ data: result || [], isHourly: false });
            }, days);
          }
        }),
        new Promise((resolve) => {
          getHistoryPipelines(auth, (result) => {
            if (result.error) resolve([]);
            else resolve(result || []);
          });
        }),
      ]);

      if (historyResult.error) {
        setError(historyResult.error);
      } else {
        setHistory(historyResult);
      }

      if (!statsResult.error) {
        if (statsResult.isHourly) {
          setHourlyStats(statsResult.data);
          setDailyStats([]);
        } else {
          setDailyStats(statsResult.data);
          setHourlyStats([]);
        }
      }

      setPipelines(pipelinesResult);
    } catch (e) {
      setError(e.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [auth, days]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Helper to format date as YYYY-MM-DD in local timezone
  const formatDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper to format hour as 12h AM/PM
  const formatHourLabel = (hour) => {
    if (hour === 0) return '12 AM';
    if (hour === 12) return '12 PM';
    return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
  };

  // Check if viewing today (hourly mode)
  const isHourlyMode = days === 0;

  // Process stats for the chart (daily or hourly)
  const chartData = useMemo(() => {
    if (isHourlyMode) {
      // Hourly view for today
      const hourMap = new Map();
      const currentHour = new Date().getHours();

      // Initialize all hours from 0 to current hour with zeros
      for (let hour = 0; hour <= currentHour; hour++) {
        hourMap.set(hour, { hour, label: formatHourLabel(hour), GO: 0, NO_GO: 0, ERROR: 0 });
      }

      // Aggregate hourly stats from backend
      hourlyStats.forEach((stat) => {
        if (selectedPipeline && stat.pipelineName !== selectedPipeline) {
          return;
        }

        const hour = stat.hour;
        if (!hourMap.has(hour)) {
          hourMap.set(hour, { hour, label: formatHourLabel(hour), GO: 0, NO_GO: 0, ERROR: 0 });
        }
        const existing = hourMap.get(hour);
        const status = stat.status || 'ERROR';
        if (status === 'GO') existing.GO += stat.count;
        else if (status === 'NO_GO') existing.NO_GO += stat.count;
        else existing.ERROR += stat.count;
      });

      return Array.from(hourMap.values()).sort((a, b) => a.hour - b.hour);
    } else {
      // Daily view
      const dateMap = new Map();
      const today = new Date();

      // Initialize all days with zeros (including today)
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateStr = formatDateKey(date);
        dateMap.set(dateStr, { date: dateStr, GO: 0, NO_GO: 0, ERROR: 0 });
      }

      // Aggregate stats from backend
      dailyStats.forEach((stat) => {
        if (selectedPipeline && stat.pipelineName !== selectedPipeline) {
          return;
        }

        const dateStr = typeof stat.date === 'string' ? stat.date : formatDateKey(new Date(stat.date));
        if (!dateMap.has(dateStr)) {
          dateMap.set(dateStr, { date: dateStr, GO: 0, NO_GO: 0, ERROR: 0 });
        }
        const existing = dateMap.get(dateStr);
        const status = stat.status || 'ERROR';
        if (status === 'GO') existing.GO += stat.count;
        else if (status === 'NO_GO') existing.NO_GO += stat.count;
        else existing.ERROR += stat.count;
      });

      return Array.from(dateMap.values()).sort((a, b) => String(a.date).localeCompare(String(b.date)));
    }
  }, [dailyStats, hourlyStats, selectedPipeline, days, isHourlyMode]);

  // Filter history by pipeline and brush range
  const filteredHistory = useMemo(() => {
    let filtered = history;

    // Filter by pipeline
    if (selectedPipeline) {
      filtered = filtered.filter((entry) => entry.pipelineName === selectedPipeline);
    }

    // Filter by brush range if set
    if (chartData.length > 0 && brushRange.endIndex !== null) {
      const startData = chartData[brushRange.startIndex];
      const endData = chartData[brushRange.endIndex];

      // Helper to convert timestamp (seconds) to Date
      const toDate = (ts) => new Date(typeof ts === 'number' ? ts * 1000 : ts);

      if (startData && endData) {
        if (isHourlyMode) {
          // Hourly mode - filter by hour
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          filtered = filtered.filter((entry) => {
            const entryDate = toDate(entry.createdAt);
            const entryHour = entryDate.getHours();
            const isToday = entryDate.toDateString() === today.toDateString();
            return isToday && entryHour >= startData.hour && entryHour <= endData.hour;
          });
        } else {
          // Daily mode - filter by date
          const startDate = new Date(startData.date);
          startDate.setHours(0, 0, 0, 0);
          const endDate = new Date(endData.date);
          endDate.setHours(23, 59, 59, 999);

          filtered = filtered.filter((entry) => {
            const entryDate = toDate(entry.createdAt);
            return entryDate >= startDate && entryDate <= endDate;
          });
        }
      }
    }

    return filtered;
  }, [history, selectedPipeline, chartData, brushRange, isHourlyMode]);

  // Paginated history for table display
  const paginatedHistory = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredHistory.slice(start, start + rowsPerPage);
  }, [filteredHistory, page, rowsPerPage]);

  // Handle brush change
  const handleBrushChange = ({ startIndex, endIndex }) => {
    setBrushRange({ startIndex, endIndex });
    setPage(0); // Reset pagination when range changes
  };

  // Reset page and brush when filters change
  useEffect(() => {
    setPage(0);
    setBrushRange({ startIndex: 0, endIndex: null });
  }, [selectedPipeline, days]);

  const handleOpenDetails = async (entry) => {
    setSelectedEntry(entry);
    setSelectedDetails(null);
    setModalLoading(true);

    await getCallHistoryEntry(auth, (result) => {
      setModalLoading(false);
      if (!result.error) {
        setSelectedDetails(result);
      }
    }, entry.id);
  };

  const handleCloseModal = () => {
    setSelectedEntry(null);
    setSelectedDetails(null);
  };

  const handleApplyTrade = (entry) => {
    if (!entry?.requestPayload) return;

    try {
      const payload = JSON.parse(entry.requestPayload);
      if (onApplyTrade && payload) {
        onApplyTrade({
          instrument: payload.instrument || entry.instrument,
          direction: payload.direction || entry.direction,
          entryPrice: payload.entryPrice,
          stopPrice: payload.stopPrice,
          targetPrice: payload.targetPrice,
          positionSize: payload.positionSize,
        });
        onBack();
      }
    } catch (e) {
      console.error('Failed to parse trade data:', e);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    // Backend sends Unix timestamp in seconds (with nanoseconds as decimal)
    // JavaScript Date expects milliseconds, so multiply by 1000
    const ms = typeof timestamp === 'number' ? timestamp * 1000 : new Date(timestamp).getTime();
    const date = new Date(ms);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Custom tooltip for chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload) return null;
    // For hourly mode, label is already formatted (e.g., "12 AM")
    // For daily mode, format the date
    const displayLabel = isHourlyMode ? label : formatDate(label);
    return (
      <div className="vhv-chart-tooltip">
        <div className="vhv-chart-tooltip-date">{displayLabel}</div>
        {payload.map((entry, index) => (
          <div key={index} className="vhv-chart-tooltip-item" style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={`validation-history-view ${isDarkMode ? 'dark' : ''}`}>
      {/* Header */}
      <div className="vhv-header">
        <button className="vhv-back-btn" onClick={onBack}>
          <ArrowBackIcon fontSize="small" />
          <span>Back to Chart</span>
        </button>
        <h2 className="vhv-title">Validation History</h2>
        <div className="vhv-header-actions">
          <select
            className="vhv-days-select"
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
          >
            <option value={0}>Today (hourly)</option>
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
          </select>
          <button className="vhv-refresh-btn" onClick={loadData} disabled={loading}>
            <RefreshIcon fontSize="small" className={loading ? 'spinning' : ''} />
          </button>
        </div>
      </div>

      {error && <div className="vhv-error">{error}</div>}

      {/* Graph Section */}
      <div className="vhv-graph-section">
        <div className="vhv-graph-container">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#333' : '#eee'} />
                <XAxis
                  dataKey={isHourlyMode ? 'label' : 'date'}
                  tickFormatter={isHourlyMode ? undefined : formatDate}
                  tick={{ fontSize: 11, fill: isDarkMode ? '#aaa' : '#666' }}
                  axisLine={{ stroke: isDarkMode ? '#444' : '#ddd' }}
                  interval={isHourlyMode ? 1 : 0}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: isDarkMode ? '#aaa' : '#666' }}
                  axisLine={{ stroke: isDarkMode ? '#444' : '#ddd' }}
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  iconType="rect"
                  iconSize={10}
                  wrapperStyle={{ fontSize: 12 }}
                />
                <Bar dataKey="GO" name="Success" fill="#27ae60" stackId="a" />
                <Bar dataKey="NO_GO" name="Failed" fill="#e74c3c" stackId="a" />
                <Bar dataKey="ERROR" name="Error" fill="#f39c12" stackId="a" />
                <Brush
                  dataKey={isHourlyMode ? 'label' : 'date'}
                  height={30}
                  stroke={isDarkMode ? '#666' : '#ccc'}
                  fill={isDarkMode ? '#1a1a1a' : '#f5f5f5'}
                  onChange={handleBrushChange}
                  tickFormatter={isHourlyMode ? undefined : formatDate}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="vhv-graph-empty">
              {loading ? 'Loading chart data...' : 'No validation data for the selected period'}
            </div>
          )}
          {chartData.length > 0 && (
            <div className="vhv-brush-hint">
              Drag the slider below the chart to filter by time range
            </div>
          )}
        </div>

        {/* Pipeline Filters */}
        <div className="vhv-pipeline-filters">
          <button
            className={`vhv-pipeline-chip ${!selectedPipeline ? 'active' : ''}`}
            onClick={() => setSelectedPipeline(null)}
          >
            All Pipelines
          </button>
          {pipelines.map((pipeline) => (
            <button
              key={pipeline.pipelineId}
              className={`vhv-pipeline-chip ${selectedPipeline === pipeline.pipelineName ? 'active' : ''}`}
              onClick={() => setSelectedPipeline(
                selectedPipeline === pipeline.pipelineName ? null : pipeline.pipelineName
              )}
            >
              {pipeline.pipelineName || 'Unnamed'}
            </button>
          ))}
        </div>
      </div>

      {/* History Table */}
      <div className="vhv-table-section">
        <div className="vhv-table-header">
          <span className="vhv-table-title">Recent Validations ({filteredHistory.length})</span>
        </div>

        {loading && filteredHistory.length === 0 ? (
          <div className="vhv-loading">Loading history...</div>
        ) : filteredHistory.length === 0 ? (
          <div className="vhv-empty">No validation calls found</div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="vhv-table-container">
              <table className="vhv-table">
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Status</th>
                    <th>Pipeline</th>
                    <th>Duration</th>
                    <th>Time</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedHistory.map((entry) => {
                    const callerInfo = getCallerTypeDisplayInfo(entry.callerType);
                    const statusInfo = getValidationStatusDisplayInfo(entry.responseStatus);

                    return (
                      <tr
                        key={entry.id}
                        className="vhv-table-row"
                        onClick={() => handleOpenDetails(entry)}
                      >
                        <td>
                          <span className="vhv-caller" style={{ color: callerInfo.color }}>
                            <CallerTypeIcon callerType={entry.callerType} />
                          </span>
                        </td>
                        <td>
                          <span
                            className="vhv-status"
                            style={{ backgroundColor: statusInfo?.color || '#95a5a6' }}
                          >
                            {entry.responseStatus || '?'}
                          </span>
                        </td>
                        <td className="vhv-pipeline">{entry.pipelineName || '-'}</td>
                        <td className="vhv-duration">{entry.durationMs ? `${entry.durationMs}ms` : '-'}</td>
                        <td className="vhv-time">{formatTimestamp(entry.createdAt)}</td>
                        <td>
                          <button
                            className="vhv-apply-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApplyTrade(entry);
                            }}
                            title="Load this trade"
                          >
                            <RestoreIcon fontSize="small" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="vhv-mobile-cards">
              {paginatedHistory.map((entry) => {
                const callerInfo = getCallerTypeDisplayInfo(entry.callerType);
                const statusInfo = getValidationStatusDisplayInfo(entry.responseStatus);

                return (
                  <div
                    key={entry.id}
                    className="vhv-mobile-card"
                    onClick={() => handleOpenDetails(entry)}
                  >
                    <div className="vhv-mobile-card-row">
                      <div className="vhv-mobile-card-left">
                        <span className="vhv-caller" style={{ color: callerInfo.color }}>
                          <CallerTypeIcon callerType={entry.callerType} />
                        </span>
                        <span
                          className="vhv-status"
                          style={{ backgroundColor: statusInfo?.color || '#95a5a6' }}
                        >
                          {entry.responseStatus || '?'}
                        </span>
                      </div>
                      <div className="vhv-mobile-card-right">
                        <span className="vhv-mobile-card-time">{formatTimestamp(entry.createdAt)}</span>
                        <button
                          className="vhv-apply-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApplyTrade(entry);
                          }}
                          title="Load this trade"
                        >
                          <RestoreIcon fontSize="small" />
                        </button>
                      </div>
                    </div>
                    <div className="vhv-mobile-card-row">
                      <span className="vhv-mobile-card-pipeline">
                        {entry.pipelineName || 'No pipeline'}
                      </span>
                      <span className="vhv-mobile-card-duration">
                        {entry.durationMs ? `${entry.durationMs}ms` : '-'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {filteredHistory.length > 0 && (
          <TablePagination
            component="div"
            count={filteredHistory.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            className="vhv-pagination"
          />
        )}
      </div>

      {/* Details Modal */}
      {selectedEntry && (
        <ValidationCallHistoryModal
          entry={selectedEntry}
          details={modalLoading ? null : selectedDetails}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default ValidationHistoryView;
